/**
 * @module API
 */

import "shared/common/translations/i18n";
import "./fonts.css";

import { init as initApm } from "@elastic/apm-rum";
import { ThemeProvider } from "@mui/material";
import ReactDOM, { Root } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "shared/common/contexts/AuthProvider";
import { monitor } from "shared/common/services/apm-logging";
import {
  accessTokenResponseType,
  getAccessToken,
} from "shared/common/services/m2m";
export { getAccessToken } from "shared/common/services/m2m";
import "./App.css";

import { SnackbarProvider } from "shared/common/contexts/SnackbarProvider";
import {
  refreshTokenType,
  tokenType,
  tokenTypeWithProvider,
} from "shared/common/services/Login";
import { Manager } from "shared/common/services/manager";
import { createCustomTheme } from "shared/common/utils/theme/theme";
import ClientConfig from "shared/config";
import MultiTenantLogin from "shared/ui/pages/login/MultiTenantLogin";

import App from "./App";

// Initializing Elastic APM
const apm = initApm({
  serviceName: `infra3d-webclient-api-${ClientConfig.stage}`,
  serverUrl:
    "https://235afb67d66d4d529a6201b796a6c034.apm.eu-west-1.aws.cloud.es.io:443",
  flushInterval: 10000,
  active: true,
});

// Manager instance
const manager = new Manager(apm);
let root: Root | null = null;

/**
 * Initializes the app on the specified HTML element and optionally provides authentication using an app access token.
 * App access tokens are obtained through app credentials and are used to authenticate the app to access infra3D services.
 * @param {string} rootId - The HTML element ID where the app will be rendered.
 * @param {string} [token=""] - The authentication token. If provided, it will be used for authentication.
 * @param {Object} [userContext] - An optional object containing user context data such as id, username, and email.
 * @param {function} [onLogout] - Optional callback function fired when logout occurs
 * @returns {Promise<Manager>} A Promise that resolves with a {@link Manager} instance once the app is loaded.
 */
export async function init(
  rootId: string,
  token: string,
  userContext?: {
    id?: string;
    username?: string;
    email?: string;
  },
  onLogout?: () => void
): Promise<Manager> {
  const tokens_json = {
    access_token: token ? token : "",
    expires_in: 1800,
    id_token: "",
    refresh_token: "",
    token_type: "Bearer",
  };
  return await _init(rootId, tokens_json, userContext, onLogout);
}

function _init(
  rootId: string,
  tokens_json: tokenType,
  userContext?: {
    id?: string;
    username?: string;
    email?: string;
  },
  onLogout?: () => void
): Promise<Manager> {
  monitor(apm, "api-initialized");
  return new Promise((resolve, reject) => {
    const onLoaded = () => {
      try {
        manager.setTokens(tokens_json);
        manager.setApmUserContext(userContext);
        resolve(manager);
      } catch (error) {
        reject(error);
      }
    };
    const element = document.getElementById(rootId) as HTMLElement;
    if (root) {
      destroy();
    }

    // Append custom styles to the element
    const style = document.createElement("style");
    style.id = "infra3d-box-sizing-styles";
    style.innerHTML = `
      #${rootId} {
      box-sizing: content-box;
      }
      #${rootId} *:not(.MuiButton-root):not(.MuiIconButton-root):not(.MuiTypography-root):not(.MuiInputBase-root) {
      box-sizing: content-box;
      }
    `;
    document.head.appendChild(style);

    root = ReactDOM.createRoot(element);
    root.render(
      <BrowserRouter>
        <AuthProvider onLogout={onLogout}>
          <ThemeProvider theme={createCustomTheme(element)}>
            <SnackbarProvider>
              <App
                apm={apm}
                manager={manager}
                onLoaded={onLoaded}
                userContext={userContext}
              />
            </SnackbarProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    );
  });
}

/**
 * Destroys the app instance and unmounts the React component from the specified HTML element.
 * This function is useful for cleaning up resources and preventing memory leaks when the app is no longer needed.
 */
export function destroy() {
  manager.getViewer()?.destroy();
  _unmount();
}

function _unmount() {
  document.getElementById("infra3d-box-sizing-styles")?.remove();
  if (root) {
    root.unmount();
    root = null;
  } else {
    console.warn("No React root found on the specified container.");
  }
}

/**
 * This function returns the {@link Manager} instance, providing a way to access and use it throughout your application.
 * @returns The {@link Manager} instance.
 */
export function getManager(): Manager {
  monitor(apm, "api.getManager");
  return manager;
}

/**
 * Retrieves a guest access token from the specified OAuth2 token endpoint.
 * Guest access tokens are used to authenticate you as a guest for infra3D services.
 * @returns {Promise<accessTokenResponseType>} A Promise that resolves with the guest access token response.
 */
export async function getGuestAccessToken(): Promise<accessTokenResponseType> {
  return getAccessToken(
    "https://infra3d-trials.auth.eu-west-1.amazoncognito.com/oauth2/token",
    "6a4ojbe03jrcfsfvp0m8gothf7",
    "15tngvrh52v6isue5gsl7ea02tp01nm3ervq1balpgrfr2292lcq"
  );
}

/**
 * Initiates an interactive login process to obtain an access token.
 * This function renders a login UI in the specified HTML element and resolves with the obtained tokens upon successful login.
 * @param {string} rootId - The HTML element ID where the login UI will be rendered.
 * @param {string} [tenant] - An optional tenant identifier for multi-tenant login scenarios. The tenant identifier can be given by our support team.
 * @param {function} [onLogout] - Optional callback function fired when logout occurs
 * @returns {Promise<tokenTypeWithProvider>} A Promise that resolves with the obtained tokens after a successful login.
 */
export async function getAccessTokenFromInteractiveLogin(
  rootId: string,
  tenant?: string,
  onLogout?: () => void
): Promise<tokenTypeWithProvider> {
  return new Promise((resolve) => {
    const element = document.getElementById(rootId) as HTMLElement;
    if (root) {
      _unmount();
      element.innerHTML = "";
    }

    const onLogin = (tokens: tokenTypeWithProvider) => {
      resolve(tokens);
      _unmount();
      element.innerHTML = "";
    };

    // Append custom styles to the element
    const style = document.createElement("style");
    style.id = "infra3d-box-sizing-styles";
    style.innerHTML = `
      #${rootId} {
      box-sizing: content-box;
      }
      #${rootId} *:not(.MuiButton-root):not(.MuiIconButton-root):not(.MuiTypography-root):not(.MuiInputBase-root) {
      box-sizing: content-box;
      }
    `;
    document.head.appendChild(style);

    root = ReactDOM.createRoot(element);
    root.render(
      <BrowserRouter>
        <AuthProvider onLogout={onLogout}>
          <ThemeProvider theme={createCustomTheme(element)}>
            <SnackbarProvider>
              <MultiTenantLogin
                onLogin={onLogin}
                tenant={tenant}
                mode={"popup"}
              />
            </SnackbarProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    );
  });
}

/**
 * Refreshes the access token using the provided refresh token.
 * This function sends a request to the OAuth2 token endpoint to obtain a new access token.
 * @param {string} url - The OAuth2 token endpoint URL.
 * @param {string} clientId - The client identifier for the OAuth2 application.
 * @param {string} refreshToken - The refresh token used to obtain a new access token.
 * @returns {Promise<refreshTokenType>} A Promise that resolves with the new access token response.
 */
export async function getAccessTokenFromRefresh(
  url: string,
  clientId: string,
  refreshToken: string
): Promise<refreshTokenType> {
  const res = await fetch(`${url}/oauth2/token`, {
    method: "POST",
    headers: new Headers({
      "content-type": "application/x-www-form-urlencoded",
    }),
    body: Object.entries({
      grant_type: "refresh_token",
      client_id: clientId,
      refresh_token: refreshToken,
    })
      .map(([k, v]) => `${k}=${v}`)
      .join("&"),
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.error);
  }
  return json;
}

/**
 * Replaces the access token with the provided token.
 * Access tokens are refreshed to maintain authentication validity without requiring re-authentication.
 * @param {string} token - The access token to be replaced.
 */
export function refreshToken(token: string) {
  const tokens_json = {
    access_token: token ? token : "",
    expires_in: 1800,
    id_token: "",
    refresh_token: "",
    token_type: "Bearer",
  };
  manager.setTokens(tokens_json);
}
